import React, { useContext, useMemo } from "react";
import Head from "next/head";

import { Context } from "../../../context";
import { getApp } from "../../../utils/api";
import {
  pageStyle,
  largeFont,
  mediumFont,
  smallFont,
  tinyFont,
  maxSection1,
  maxSection2,
} from "../../../utils/styles";
import Layout from "../../../components/Layout";
import BannerAd from "../../../components/BannerAd";
import OverlayImage from "../../../components/OverlayImage";
import Image from "../../../components/Image";
import ItemsGrid from "../../../components/ItemsGrid";
import ReadMore from "../../../components/ReadMore";
import Rating from "../../../components/Rating";
import SocialShare from "../../../components/SocialShare";
import GoogleAd from "../../../components/GoogleAd";
import Page404 from "../../404";

const Provider = ({ app, apps, provider }) => {
  const { theme } = useContext(Context);

  const link = useMemo(() => app?.links.find((link) => link.slug === provider), [app]);

  if (!app || !link) {
    return <Page404 />;
  }

  return (
    <Layout>
      <Head>
        <title>{`${app.name} - AppCheeta`}</title>

      </Head>
      <div className="page-content">
        <style jsx>{pageStyle}</style>
        <style jsx>{`
          .page-body {
            display: flex;
            justify-content: center;
          }
          .section {
            margin-top: 2rem;
            margin-bottom: 3rem;
            margin-left: 1rem;
            margin-right: 1rem;
          }
          .section-title {
            font-size: ${mediumFont};
            font-weight: bold;
            margin-bottom: 0.75rem;
          }
          .app-image-name {
            display: flex;
            align-items: center;
          }
          .app-image {
            width: 130px;
            margin-right: 15px;
          }
          .app-name {
            width: calc(100% - 145px);
          }
          .app-title {
            font-size: ${mediumFont};
            font-weight: bold;
            margin-bottom: 0.5rem;
          }
          .app-social {
            display: flex;
            justify-content: space-between;
            flex-direction: column;
          }
          .app-social h3 {
            font-size: ${tinyFont};
          }
          .app-social-share {
            margin-top: 0.8rem;
          }
          .app-rate {
            display: inline-flex;
            align-items: center;
            color: ${theme.tertiaryTextColor};
            font-size: 0.85rem;
            height: 18px;
            margin-top: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .app-links {
            display: flex;
            justify-content: center;
          }
          .app-link {
            padding: 10px;
            border: 1px solid ${theme.primaryTextColor};
            margin: 0.25rem;
            color: ${theme.primaryTextColor};
            text-decoration: none;
            text-align: center;
            min-width: 50%;
          }
          .app-link:hover,
          .app-link:focus,
          .app-link:active {
            opacity: 0.8;
          }
          .comma-text:not(:first-child):before {
            content: ", ";
          }
          @media screen and (max-width: ${maxSection2}px) {
            .page-body {
              display: block;
            }
          }
          @media screen and (min-width: ${maxSection1}px) {
            .section {
              margin-top: 2.5rem;
            }
            .app-image {
              width: 170px;
              margin-right: 30px;
            }
            .app-name {
              width: calc(100% - 200px);
            }
            .app-title {
              font-size: ${largeFont};
              margin-bottom: 0.75rem;
            }
            .app-social {
              align-items: flex-end;
              flex-direction: row;
            }
            .app-social h3 {
              font-size: ${smallFont};
            }
            .app-rate {
              margin-top: 0.75rem;
              margin-bottom: 0.75rem;
            }
          }
        `}</style>

        <BannerAd />
        <div className="page-body">
          <div className="section games">
            <div className="app-image-name">
              <div className="app-image">
                <OverlayImage
                  path={app.image}
                  width={350}
                  height={350}
                  overlay={app.name}
                  showOverlay={false}
                />
              </div>
              <div className="app-name">
                <h2 className="app-title">{app.name}</h2>
                <div className="app-social">
                  <div>
                    <h3>{app.category?.name}</h3>
                    <div className="app-rate">
                      {app.user_rating ? app.user_rating : ""}&nbsp;
                      {Boolean(app.user_rating) && <Rating star={app.user_rating} />}
                    </div>
                  </div>
                  <div className="app-social-share">
                    <SocialShare text={app.name} image={app.image} />
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <GoogleAd section="leaderboard" />
            <hr />
            <div className="app-info">
              <h2 className="section-title">Instructions</h2>
              <p>
                <p>Follow the steps below to install this app from the {link.name}:</p>
                <br />
                <p>1. Click the button below.</p>
                <p>
                  2. Tap or Click the price or Get button. Free apps show the get button and paid
                  apps show the price.
                </p>
                <p>3. The app will download to your device.</p>
                <p>
                  4. Once downloaded, the Get or price button changes to open. Click or tap the Open
                  button.
                </p>
              </p>
            </div>
            <hr />
            <div className="app-links">
              <a
                className="app-link"
                key={link.slug}
                href={link.url}
                target="_blank"
                rel="noopener"
              >
                {`Install from ${link.name}`}
              </a>
            </div>
            <hr />
            <div className="app-info">
              <h2 className="section-title">App Info</h2>
              <p>
                Platforms:{" "}
                {app.platforms.map((platform, index) => (
                  <span key={index} className="comma-text">
                    {platform}
                  </span>
                ))}
              </p>
              {/*<p>
                Requirements: {Object.keys(app.requirements).map((key, index) => (
                  <span key={index} className="comma-text">{app.requirements[key].platform}</span>
                ))}
              </p>*/}
            </div>
            <hr />
            <div className="app-info">
              <h2 className="section-title">Disclaimer</h2>
              <ReadMore
                text={`AppCheeta does not own this application. We only provide
                  images and links contained in the Google Play API, the App Store
                  Connect API, the IGDB API, and the official source press kits.
                  This site follows the Google Ads Advertising Policies, Google
                  Unwanted Software Policy, Microsoft Ads Advertising Policies,
                  and TikTok Advertising Policies. If you own this app and wish to
                  have it removed from AppCheeta, contact us. We provide only the
                  official links from the Google Play Store, Apple App Store, and
                  official sources.`}
                characterLimit={300}
                showLessButton={true}
              />
            </div>
            <GoogleAd section="leaderboard_lazy" />
            {apps.length > 0 && (
              <>
                <hr />
                <div className="app-info">
                  <h2 className="section-title">Similar Apps</h2>
                  <ItemsGrid items={apps.slice(0, 6)} path="app" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

Provider.getInitialProps = async ({ query, cached }) => {
  const { slug, provider } = query;
  const { app, apps } = await getApp({ slug, cached });
  const analytics = {
    content_group1: "App", // Page Type
  };
  return { app, apps, slug, provider, analytics };
};

export default Provider;
